import { useState, useRef, useCallback, useEffect } from 'react';
import { useCameraStream, useDeviceOrientation } from '../shared';
import type { OrientationType } from '../shared';
import type { OrientationAngle } from '../shared';

export interface VideoTake {
  id: string;
  blob: Blob;
  url: string;
  duration: number;
  orientation: OrientationType | null;
}

interface VideoRecorderState {
  isRecording: boolean;
  duration: number;
  videoBlob: Blob | null;
  videoUrl: string | null;
  recordedOrientation: OrientationType | null;
  takes: VideoTake[];
}

/** Convert angle to OrientationType for backward-compatible consumers */
function angleToOrientation(angle: OrientationAngle): OrientationType {
  return angle === 90 || angle === 270 ? 'landscape' : 'portrait';
}

export const useVideoRecorder = () => {
  // 1080p — the shared default is 720p, which is visibly soft for client
  // presentations. The transcoder downsizes the upload; capture stays the
  // high-quality master.
  const camera = useCameraStream({ audio: true, width: 1920, height: 1080 });
  const { angle: deviceAngle, requestPermission: requestOrientationPermission } = useDeviceOrientation();

  const [state, setState] = useState<VideoRecorderState>({
    isRecording: false,
    duration: 0,
    videoBlob: null,
    videoUrl: null,
    recordedOrientation: null,
    takes: [],
  });

  const [isPaused, setIsPaused] = useState(false);

  // Canvas for orientation-corrected recording
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const chunks = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isRecordingRef = useRef(false);
  const recordingAngleRef = useRef<OrientationAngle>(0);
  const facingModeRef = useRef(camera.facingMode);
  const deviceAngleRef = useRef(deviceAngle);

  // Keep refs in sync
  facingModeRef.current = camera.facingMode;
  deviceAngleRef.current = deviceAngle;

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startTimer = useCallback(() => {
    clearTimer();
    timerRef.current = setInterval(() => {
      setState(prev => ({ ...prev, duration: prev.duration + 1 }));
    }, 1000);
  }, [clearTimer]);

  const stopDrawLoop = useCallback(() => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);

  /**
   * Draw each video frame to the canvas.
   * The browser's video element already handles sensor rotation,
   * so drawImage gives us the correctly-oriented frame.
   * We just need to set the right canvas dimensions for portrait/landscape.
   * Front camera gets mirrored.
   */
  const drawFrame = useCallback(() => {
    const video = camera.videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || video.readyState < 2) {
      animationFrameRef.current = requestAnimationFrame(drawFrame);
      return;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.save();

    // Mirror for front camera
    if (facingModeRef.current === 'user') {
      ctx.translate(canvas.width, 0);
      ctx.scale(-1, 1);
    }

    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    ctx.restore();

    animationFrameRef.current = requestAnimationFrame(drawFrame);
  }, []); // stable — uses only refs

  /** Start the camera preview (no recording yet) */
  const startCamera = useCallback(async (facing?: 'user' | 'environment') => {
    await camera.startStream(facing);
  }, [camera.startStream]);

  /** Flip between front / rear camera (blocked during recording) */
  const flipCamera = useCallback(async () => {
    if (isRecordingRef.current) return;
    await camera.toggleCamera();
  }, [camera.toggleCamera]);

  /** Start recording via canvas stream */
  const startRecording = useCallback(() => {
    const canvas = canvasRef.current;
    const video = camera.videoRef.current;
    if (!canvas || !video || !camera.streamRef.current) return;

    // Lock orientation at recording start
    recordingAngleRef.current = deviceAngleRef.current;

    const vw = video.videoWidth;
    const vh = video.videoHeight;

    // Set canvas dimensions based on device orientation
    const isLandscape = recordingAngleRef.current === 90 || recordingAngleRef.current === 270;
    if (isLandscape) {
      canvas.width = Math.max(vw, vh);
      canvas.height = Math.min(vw, vh);
    } else {
      canvas.width = Math.min(vw, vh);
      canvas.height = Math.max(vw, vh);
    }

    // Start draw loop
    animationFrameRef.current = requestAnimationFrame(drawFrame);

    // Get canvas stream and add audio tracks from camera
    const canvasStream = canvas.captureStream(30);
    const audioTracks = camera.streamRef.current.getAudioTracks();
    audioTracks.forEach(track => canvasStream.addTrack(track));

    // Pick best available mime type
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : MediaRecorder.isTypeSupported('video/webm')
        ? 'video/webm'
        : 'video/mp4';

    chunks.current = [];
    const recorder = new MediaRecorder(canvasStream, {
      mimeType,
      // 8 Mbps high-quality master. The old 1.5 Mbps ("skip post-compression")
      // predates the WebCodecs transcoder and starved camera footage into
      // blocky, pumping frames. Capture high; the upload pipeline transcodes
      // down to ~3.5 Mbps. Devices without WebCodecs fall back to the legacy
      // realtime compressor, which exists for exactly that case.
      videoBitsPerSecond: 8_000_000,
    });

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunks.current.push(e.data);
      }
    };

    recorder.onstop = () => {
      const blob = new Blob(chunks.current, { type: mimeType });
      const url = URL.createObjectURL(blob);
      setState(prev => ({ ...prev, videoBlob: blob, videoUrl: url }));
    };

    recorder.start(1000); // collect data every second
    mediaRecorder.current = recorder;
    isRecordingRef.current = true;

    setState(prev => ({
      ...prev,
      isRecording: true,
      duration: 0,
      videoBlob: null,
      videoUrl: null,
      recordedOrientation: angleToOrientation(recordingAngleRef.current),
    }));
    setIsPaused(false);

    startTimer();
  }, [camera.videoRef, camera.streamRef, drawFrame, startTimer]);

  /** Pause recording */
  const pauseRecording = useCallback(() => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'recording') {
      mediaRecorder.current.pause();
      clearTimer();
      stopDrawLoop(); // save CPU while paused
      setIsPaused(true);
    }
  }, [clearTimer, stopDrawLoop]);

  /** Resume recording */
  const resumeRecording = useCallback(() => {
    if (mediaRecorder.current && mediaRecorder.current.state === 'paused') {
      mediaRecorder.current.resume();
      startTimer();
      animationFrameRef.current = requestAnimationFrame(drawFrame);
      setIsPaused(false);
    }
  }, [startTimer, drawFrame]);

  /** Stop recording (keeps stream alive for preview) */
  const stopRecording = useCallback(() => {
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }
    clearTimer();
    stopDrawLoop();
    isRecordingRef.current = false;
    setIsPaused(false);
    setState(prev => ({ ...prev, isRecording: false }));
  }, [clearTimer, stopDrawLoop]);

  /** Stop the camera stream entirely */
  const stopCamera = useCallback(() => {
    if (mediaRecorder.current && mediaRecorder.current.state !== 'inactive') {
      mediaRecorder.current.stop();
    }
    camera.stopStream();
    clearTimer();
    stopDrawLoop();
    isRecordingRef.current = false;
    setIsPaused(false);
    setState(prev => ({ ...prev, isRecording: false }));
  }, [camera.stopStream, clearTimer, stopDrawLoop]);

  /** Accept current recording into the takes array (called on "Use Video") */
  const acceptTake = useCallback(() => {
    setState(prev => {
      if (!prev.videoBlob || !prev.videoUrl) return prev;
      const take: VideoTake = {
        id: `take-${Date.now()}`,
        blob: prev.videoBlob,
        url: prev.videoUrl,
        duration: prev.duration,
        orientation: prev.recordedOrientation,
      };
      return {
        ...prev,
        takes: [...prev.takes, take],
        // Clear current recording — it's now in takes
        videoBlob: null,
        videoUrl: null,
        duration: 0,
      };
    });
  }, []);

  /** Delete a specific take by ID */
  const deleteTake = useCallback((takeId: string) => {
    setState(prev => {
      const take = prev.takes.find(t => t.id === takeId);
      if (take) URL.revokeObjectURL(take.url);
      return { ...prev, takes: prev.takes.filter(t => t.id !== takeId) };
    });
  }, []);

  /** Inject pre-recorded takes (e.g. from a restored draft) */
  const injectTakes = useCallback((takes: VideoTake[]) => {
    setState(prev => ({ ...prev, takes }));
  }, []);

  /** Legacy inject for single video — adds as a take */
  const injectVideo = useCallback((blob: Blob, dur: number, orientation: OrientationType | null) => {
    const url = URL.createObjectURL(blob);
    const take: VideoTake = {
      id: `draft-${Date.now()}`,
      blob,
      url,
      duration: dur,
      orientation,
    };
    setState(prev => ({ ...prev, takes: [...prev.takes, take] }));
  }, []);

  /** Reset current recording for a retake (keeps stream + takes alive) */
  const reset = useCallback(() => {
    setIsPaused(false);
    setState(prev => {
      if (prev.videoUrl) URL.revokeObjectURL(prev.videoUrl);
      return {
        ...prev,
        duration: 0,
        videoBlob: null,
        videoUrl: null,
      };
    });
  }, []);

  /** Full reset and stop — clears takes too */
  const resetAll = useCallback(() => {
    stopCamera();
    setState(prev => {
      if (prev.videoUrl) URL.revokeObjectURL(prev.videoUrl);
      prev.takes.forEach(t => URL.revokeObjectURL(t.url));
      return {
        isRecording: false,
        duration: 0,
        videoBlob: null,
        videoUrl: null,
        recordedOrientation: null,
        takes: [],
      };
    });
  }, [stopCamera]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopDrawLoop();
      clearTimer();
    };
  }, [stopDrawLoop, clearTimer]);

  return {
    ...state,
    isPaused,
    isReady: camera.isReady,
    facingMode: camera.facingMode,
    error: camera.error,
    videoRef: camera.videoRef,
    canvasRef,
    startCamera,
    flipCamera,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    acceptTake,
    deleteTake,
    injectTakes,
    injectVideo,
    stopCamera,
    reset,
    resetAll,
    requestOrientationPermission,
  };
};
